import type { ReactNode } from "react";

import type { BadgeProps, BannerProps } from "@alpacahq/ui/old";

import type {
  Account,
  DocumentRequestResponse,
  Margin,
  OwnerDetails,
} from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { ActionRequired } from "./ActionRequired";
import { Active } from "./Active";
import { ApprovalPending } from "./ApprovalPending";
import { Submitted } from "./Submitted";

const BANNER_ACCOUNT_STATUSES = [
  "PAPER_ONLY",
  "ONBOARDING",
  "SUBMITTED",
  "APPROVED",
  "APPROVAL_PENDING",
  "ACCOUNT_CLOSED",
  "ACTION_REQUIRED",
  "ACTIVE",
] as const;

export type BannerAccountStatus = (typeof BANNER_ACCOUNT_STATUSES)[number];

export interface AccountStatusProps {
  account?: Account;
  details?: OwnerDetails;
  margin?: Margin;
  accountDocumentRequests?: DocumentRequestResponse[];
  isLegalEntity?: boolean;
}

export type AccountStatusMapFunction = (props: AccountStatusProps) => {
  title?: string;
  message?: ReactNode;
  button?: { text: string; to: string };
  badge?: { title: string; variant: BadgeProps["variant"] };
  variant?: BannerProps["variant"];
} | null;

export type AccountStatusMap = Record<
  BannerAccountStatus,
  AccountStatusMapFunction
>;

export const ACCOUNT_STATUS_MAP: AccountStatusMap = {
  PAPER_ONLY: () => ({
    message: "Apply for your Live account",
    button: {
      text: "Apply Now →",
      to: Path.ROUTE_NEW_ACCOUNT,
    },
  }),
  ONBOARDING: () => ({
    message: "Complete your account application",
    button: {
      text: "Complete Application →",
      to: Path.ROUTE_NEW_ACCOUNT,
    },
  }),
  SUBMITTED: Submitted,
  APPROVED: () => ({
    variant: "success",
    title: "Application approved!",
    message: "One moment, setting up your account",
  }),
  APPROVAL_PENDING: ApprovalPending,
  ACCOUNT_CLOSED: () => ({
    variant: "warning",
    message: "Your account was closed",
  }),
  ACTION_REQUIRED: ActionRequired,
  ACTIVE: Active,
};
